import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { useEmployee, useEmployeeAuth } from './hooks/useEmployee';
import { useGeolocation } from './hooks/useGeolocation';
import { OvertimeModal, ProxyReasonModal } from './components/Modal';
import Register from './components/Register';
import PWAInstallPrompt from './PWAInstallPrompt';
import api from './services/api';

function App() {
  // 店鋪列表狀態
  const [locationList, setLocationList] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // 員工名單狀態
  const [employeeList, setEmployeeList] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);

  // 登入狀態
  const { employeeName, login, isLoggedIn } = useEmployeeAuth();

  // 打卡狀態
  const { status, clockTime, workHours, isLoading: statusLoading, updateAfterClockIn } = useEmployee(employeeName);

  // GPS 定位 - 含重試與降級機制
  const { position, error: geoError, isLoading: geoLoading, positionStatus, getPosition, getPositionWithRetry } = useGeolocation();

  // 計算兩點間距離 (Haversine formula)
  const calcDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => d * (Math.PI / 180);
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  }, []);

  // UI 狀態
  const [locationId, setLocationId] = useState('');
  const [nfcToken, setNfcToken] = useState('');  // NFC Token（隱藏不顯示）
  const [isProxyMode, setIsProxyMode] = useState(false);
  const [targetEmployee, setTargetEmployee] = useState(null);
  const [selectedProxyReason, setSelectedProxyReason] = useState(null);

  // 打卡冷卻時間狀態
  const [cooldown, setCooldown] = useState(0); // 剩餘秒數
  const [clockInTime, setClockInTime] = useState(null); // 成功打卡後的時間

  // NFC 重新整理提醒狀態
  const [showNFCReminder, setShowNFCReminder] = useState(false);

  // NFC 感應開啟瀏覽器時的引導提示
  // 若 NFC 感應開啟了頁面（URL 帶 loc 參數）但不在 PWA 獨立模式，
  // 顯示提示引導用戶從主畫面圖示開啟（Android WebAPK 可直接喚醒；iOS 需手動開啟）
  const [showNFCBrowserHint, setShowNFCBrowserHint] = useState(false);

  // 從網址擷取 NFC 參數（?loc=TW&token=xxx）
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const loc = urlParams.get('loc');
    const token = urlParams.get('token');
    if (loc) setLocationId(loc);

    // NFC 感應開啟（URL 帶 loc）但不在獨立模式 → 顯示引導提示
    if (loc) {
      const isStandaloneMode =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches ||
        window.navigator.standalone === true; // iOS Safari
      if (!isStandaloneMode) {
        setShowNFCBrowserHint(true);
      }
    }
    if (token) {
      // 第一次載入：URL 帶有 token，表示使用者剛掃描完 NFC
      // 清除 URL 中的 token，避免重新整理後仍然存在
      const newUrl = window.location.pathname + (window.location.search ? window.location.search.replace(/[?&]token=[^&]*/, '') : '');
      window.history.replaceState({}, '', newUrl);
      setNfcToken(token);
      // 記錄已掃描過 NFC（用於偵測重新整理）
      sessionStorage.setItem('nfcScanned', 'true');
      // 記錄 token 使用時間，用於有效期檢查
      localStorage.setItem('nfcTokenTimestamp', Date.now().toString());
      // 第一次載入不顯示提醒（使用者剛掃描完）
      setShowNFCReminder(false);
    } else {
      // 重新整理後：URL 沒有 token
      // 若先前已掃描過 NFC，表示這是重新整理，需要再次接觸 NFC
      const nfcScanned = sessionStorage.getItem('nfcScanned');
      if (nfcScanned === 'true') {
        setShowNFCReminder(true);
      }
    }
  }, []);

  // 檢測網路狀態
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 即時距離（公尺）
  const location = locationList.find((loc) => loc.id === locationId);
  const distance = (position && location)
    ? calcDistance(position.latitude, position.longitude, location.latitude, location.longitude)
    : null;

  // 可以在 <50m 內打卡？（必須有 NFC Token）
  const DISTANCE_LIMIT = 50;
  // 離線模式下允許打卡（資料會儲存在IndexedDB，上線後同步）
  const canClock = !!(position && locationId && nfcToken && (distance !== null && distance <= DISTANCE_LIMIT) || !isOnline);

  // NFC 提醒顯示相關
  // 若 nfcToken 從空變為有值（例如使用者重新掃描），則隱藏提醒
  useEffect(() => {
    if (nfcToken && showNFCReminder) {
      setShowNFCReminder(false);
    }
  }, [nfcToken, showNFCReminder]);

  // Modal 狀態
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [showProxyModal, setShowProxyModal] = useState(false);

  // 打卡狀態
  const [isClocking, setIsClocking] = useState(false);
  const [result, setResult] = useState(null);

  // 工時計時器清除用 ref
  const workHoursTimerRef = useRef(null);
  const [liveWorkHours, setLiveWorkHours] = useState(0);

  // 登入後自動定位一次（讓距離顯示與打卡按鈕可用）
  const autoLocatedRef = useRef(false);
  useEffect(() => {
    if (isLoggedIn && !autoLocatedRef.current) {
      autoLocatedRef.current = true;
      getPosition();
    }
  }, [isLoggedIn, getPosition]);

  // 工時計算器：定時更新顯示
  useEffect(() => {
    if (status !== '下班' && clockTime) {
      // 清除舊計時器
      if (workHoursTimerRef.current) {
        clearInterval(workHoursTimerRef.current);
      }

      // 建立新計時器
      workHoursTimerRef.current = setInterval(() => {
        const clockIn = new Date(clockTime);
        const now = new Date();
        const diffHours = (now - clockIn) / (1000 * 60 * 60);
        setLiveWorkHours(Math.round(diffHours * 10) / 10);
      }, 30000); // 每30秒更新一次，減少渲染次數

      // 初始計算
      const clockIn = new Date(clockTime);
      const now = new Date();
      const diffHours = (now - clockIn) / (1000 * 60 * 60);
      setLiveWorkHours(Math.round(diffHours * 10) / 10);
    } else {
      setLiveWorkHours(0);
    }

    // 清除計時器
    return () => {
      if (workHoursTimerRef.current) {
        clearInterval(workHoursTimerRef.current);
        workHoursTimerRef.current = null;
      }
    };
  }, [status, clockTime]);

  // 從 API 取得店鋪列表
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.getLocations();
        if (response.success) {
          setLocationList(response.data || []);
        }
      } catch (err) {
        console.error('取得店鋪列表失敗:', err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // 從 API 取得員工名單
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.getEmployees();
        if (response.success) {
          setEmployeeList(response.data || []);
        }
      } catch (err) {
        console.error('取得員工名單失敗:', err);
      } finally {
        setLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, []);

  // 處理店鋪選擇
  const handleLocationChange = (e) => {
    setLocationId(e.target.value);
  };

  // 取得打卡類型
  const clockType = status === '上班' ? '上班' : '下班';

  // 處理打卡 - 點擊時才抓取 GPS（含重試與降級機制）
  const handleClockIn = useCallback(async (extraData = {}) => {
    if (!locationId) {
      alert('未偵測到打卡地點，請使用 NFC 感應打卡');
      return;
    }

    // 檢查是否需要 NFC Token
    if (!nfcToken) {
      setShowNFCReminder(true);
      alert('未偵測到 NFC Token，請使用 NFC 感應打卡');
      return;
    }

    // 檢查 token 是否仍然有效（例如：5 分鐘內）
    const tokenTimestamp = localStorage.getItem('nfcTokenTimestamp');
    if (tokenTimestamp) {
      const now = Date.now();
      const tokenAge = now - parseInt(tokenTimestamp);
      if (tokenAge > 300000) { // 5 分鐘
        alert('NFC Token 已過期，請重新掃描 NFC 標籤');
        setNfcToken('');
        localStorage.removeItem('nfcTokenTimestamp');
        setShowNFCReminder(true);
        return;
      }
    }

    setIsClocking(true);
    setResult(null);

    // 使用重試 + 降級機制取得 GPS 位置
    let currentPosition = await getPositionWithRetry();

    if (!currentPosition) {
      alert('無法取得 GPS 位置，請確認已開啟定位權限後移至窗邊或室外重試');
      setIsClocking(false);
      return;
    }

    try {
      const response = await api.clockIn({
        locationId,
        token: nfcToken,
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
        accuracy: currentPosition.accuracy,
        employeeName: targetEmployee?.name || employeeName,
        clockType,
        isProxy: isProxyMode,
        operatorName: isProxyMode ? employeeName : null,
        proxyReason: isProxyMode ? selectedProxyReason : null,
        ...extraData
      });

      if (response.success) {
        setResult({ type: 'success', message: response.message });

        // 觸發打卡成功動畫
        const btn = document.querySelector('.clock-button');
        if (btn) {
          btn.classList.add('success');
          setTimeout(() => btn.classList.remove('success'), 800);
        }

        // 更新本地狀態
        const timestamp = response.data?.timestamp || new Date().toISOString().replace('T', ' ').substring(0, 19);
        updateAfterClockIn(clockType, timestamp);

        // 設置打卡時間
        setClockInTime(timestamp);

        // 設置 5 分鐘冷卻時間
        setCooldown(300);

        // 代打卡模式：打卡後重置
        if (isProxyMode) {
          setTimeout(() => {
            setIsProxyMode(false);
            setTargetEmployee(null);
            setSelectedProxyReason(null);
          }, 2000);
        }

        // 清除 NFC 提醒，因為已成功打卡
        setShowNFCReminder(false);

        // 清除 NFC Token，避免重新整理後仍然存在
        setNfcToken('');
        localStorage.removeItem('nfcTokenTimestamp');
      } else {
        setResult({ type: 'error', message: response.message });
        setShowNFCReminder(true);
      }
    } catch (err) {
      setResult({ type: 'error', message: '網路錯誤，請重試' });
      setShowNFCReminder(true);
    } finally {
      setIsClocking(false);
    }
  }, [locationId, nfcToken, employeeName, clockType, isProxyMode, targetEmployee, selectedProxyReason, updateAfterClockIn, getPositionWithRetry]);

  // 下班打卡前檢查工時
  const handleClockOutCheck = () => {
    const displayHours = clockTime ? liveWorkHours : workHours;
    if (displayHours > 9.5) {
      setShowOvertimeModal(true);
    } else {
      handleClockIn();
    }
  };

  // 加班/晚餐 Modal 確認
  const handleOvertimeConfirm = ({ isOvertime, hasDinner }) => {
    setShowOvertimeModal(false);
    handleClockIn({
      overtime: isOvertime ? '是' : '否',
      dinner: isOvertime ? (hasDinner ? '是' : '否') : null
    });
  };

  // 代打卡原因 Modal 確認
  const handleProxyReasonConfirm = (reason) => {
    setSelectedProxyReason(reason);
    setShowProxyModal(false);
    handleClockIn();
  };

  // GPS 定位按鈕（只在點擊時抓取位置）
  const handleGetLocation = async () => {
    await getPosition();
  };

  // 註冊頁（未登入時顯示）
  if (!isLoggedIn) {
    return <Register onRegistered={login} />;
  }

  // 顯示用的工時（使用 liveWorkHours 代替 workHours）
  const displayWorkHours = clockTime ? liveWorkHours : workHours;

  // 計算冷卻時間顯示
  const getCooldownDisplay = () => {
    if (cooldown <= 0) return null;
    const minutes = Math.floor(cooldown / 60);
    const seconds = cooldown % 60;
    return `${minutes}分${seconds}秒`;
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <h1>🏢 NFC 打卡系統</h1>
        <div className="user-info">
          <span>👤 {employeeName}</span>
        </div>
      </header>

      <main className="app-main">
        {/* 代打卡開關 */}
        <div className="proxy-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={isProxyMode}
              onChange={(e) => setIsProxyMode(e.target.checked)}
            />
            <span className="slider"></span>
          </label>
          <span>代打卡模式</span>
        </div>

        {/* 代打卡：選擇目標員工 */}
        {isProxyMode && (
          <div className="proxy-section">
            <label>選擇要代打卡的員工：</label>
            <select
              value={targetEmployee?.name || ''}
              onChange={(e) => {
                const emp = (employeeList || []).find((em) => em.name === e.target.value);
                setTargetEmployee(emp);
              }}
            >
              <option value="">請選擇</option>
              {(employeeList || [])
                .filter((emp) => emp.name !== employeeName)
                .map((emp) => (
                  <option key={emp.name} value={emp.name}>{emp.name}</option>
                ))}
            </select>
          </div>
        )}

        {/* 打卡按鈕區域 */}
        <div className="clock-section">
          {/* 網路狀態指示器 */}
          {!isOnline && (
            <div className="offline-indicator">
              ⚠️ 離線模式 - 打卡資料將在連線後自動同步
            </div>
          )}
          
          {/* 店鋪顯示（NFC 自動帶入，隱藏下拉選單） */}
          <div className="location-section">
            {locationId ? (
              <p className="location-fixed">📍 打卡店鋪：{location?.name || locationId}</p>
            ) : (
              <p className="location-warning">⚠️ 請使用 NFC 感應打卡貼紙</p>
            )}
          </div>

            {/* NFC 提醒橫幅 */}
        {showNFCReminder && (
          <div className="nfc-reminder-banner">
            <p>🔐 請再次接觸 NFC 標籤以取得打卡權限</p>
          </div>
        )}

        {/* NFC 瀏覽器開啟引導橫幅 */}
        {showNFCBrowserHint && (
          <div className="nfc-browser-hint-banner">
            <p>
              📱 偵測到您是從瀏覽器開啟打卡系統。
              {/iPad|iPhone|iPod/.test(navigator.userAgent) ||
              (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
                ? 'iOS 系統限制，NFC 感應會先開啟 Safari，請從主畫面圖示開啟打卡系統以獲得完整體驗。'
                : '請使用 Chrome 選單中的「安裝應用程式」安裝 PWA，安裝後 NFC 感應將直接喚醒應用。'}
            </p>
            <button
              className="nfc-hint-close"
              onClick={() => setShowNFCBrowserHint(false)}
            >
              ✕
            </button>
          </div>
        )}

          <div className="clock-button-container">
            <button
              className={`clock-button ${clockType === '上班' ? 'btn-in' : 'btn-out'}`}
              onClick={clockType === '上班' ? () => handleClockIn() : handleClockOutCheck}
              disabled={isClocking || statusLoading || cooldown > 0 || (!canClock && isOnline)}
              title={!isOnline ? "離線模式下可打卡" : ""}
            >
              {isClocking ? '打卡中...' : cooldown > 0 ? `冷卻中 (${getCooldownDisplay()})` : (isOnline ? '打卡' : '離線打卡')}
            </button>
          </div>

          {/* 打卡成功後顯示打卡時間 */}
          {clockInTime && (
            <div className="clock-time-display">
              <p className="clock-in-time">打卡時間：{clockInTime}</p>
            </div>
          )}

          {/* 即時距離顯示 */}
          <div className="distance-display">
            {!position ? (
              <p className="distance-msg">📍 尚未定位，無法判定距離</p>
            ) : !locationId ? (
              <p className="distance-msg">📍 請使用 NFC 感應打卡</p>
            ) : distance !== null ? (
              <div className="distance-info">
                <p className={`distance-value ${distance <= DISTANCE_LIMIT || !isOnline ? 'in-range' : 'out-range'}`}>
                  📍 距離 {location?.name || locationId} {distance} 公尺
                </p>
                {!isOnline ? (
                  <p className="distance-ok">📶 離線模式 - 距離限制已解除</p>
                ) : distance <= DISTANCE_LIMIT ? (
                  <p className="distance-ok">✅ 已在打卡範圍內</p>
                ) : (
                  <p className="distance-fail">
                    ⚠️ 超出 {DISTANCE_LIMIT} 公尺範圍，請移至店舖附近
                  </p>
                )}
              </div>
            ) : null}
          </div>

          {/* 錯誤/成功訊息 */}
          {result && (
            <div className={`result-message ${result.type}`}>
              {!isOnline ? '📶 ' : ''}{result.message}
              {!isOnline && result.type === 'success' && '（已儲存至本地，連線後同步）'}
            </div>
          )}
        </div>

        {/* 資訊區域 */}
        <div className="info-section">
          <div className="info-card">
            <h4>今日工時</h4>
            <p className="work-hours">{displayWorkHours.toFixed(1)} 小時</p>
          </div>
          <div className="info-card">
            <h4>GPS 狀態</h4>
            {geoError ? (
              <p className="status-error">{geoError}</p>
            ) : positionStatus === 'locating' ? (
              <p className="status-loading">⏳ 定位中...</p>
            ) : positionStatus === 'low_accuracy' ? (
              <p className="status-warning">⚠️ 定位成功（室內模式）</p>
            ) : position ? (
              <p className="status-ok">✅ 已定位</p>
            ) : (
              <p className="status-loading">尚未定位</p>
            )}
            {position && (
              <p className="accuracy-info">
                精準度：{position.accuracy < 50
                  ? <span className="accuracy-good">約 {position.accuracy} 公尺 ✓</span>
                  : position.accuracy < 100
                    ? <span className="accuracy-fair">約 {position.accuracy} 公尺 ⚠</span>
                    : <span className="accuracy-poor">約 {position.accuracy} 公尺 ✗</span>
                }
              </p>
            )}
            <button className="btn-retry" onClick={handleGetLocation} disabled={geoLoading || positionStatus === 'locating'}>
              {geoLoading || positionStatus === 'locating' ? '定位中...' : '獲取 GPS 位置'}
            </button>
          </div>
        </div>

        {/* 代打卡：選擇原因按鈕 */}
        {isProxyMode && targetEmployee && !selectedProxyReason && (
          <button className="btn-proxy-reason" onClick={() => setShowProxyModal(true)}>
            選擇代打卡原因
          </button>
        )}
      </main>

      {/* Modals */}
      <OvertimeModal
        isOpen={showOvertimeModal}
        onConfirm={handleOvertimeConfirm}
        onCancel={() => setShowOvertimeModal(false)}
      />

      <ProxyReasonModal
        isOpen={showProxyModal}
        onConfirm={handleProxyReasonConfirm}
        onCancel={() => setShowProxyModal(false)}
      />

      {/* PWA安裝提示 */}
      <PWAInstallPrompt />
    </div>
  );
}

export default App;